import { useEffect, useState, useCallback } from "react";
import { Loader2, Activity, AlertTriangle, RefreshCw, Save, Pencil, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// PLD Bloque 1 · Pieza 1 — Perfil transaccional del cliente (43ª-V / Disposición 21ª).
// Muestra el perfil ESTIMADO (declarado al inicio) vs el REAL (calculado de pagos/operaciones)
// y las alertas por desviación. La decisión de reclasificar el riesgo es del Oficial de Cumplimiento.
const WRITE_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld", "ejecutivo_credito", "analista_credito"];
const MONITOR_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld"];

export function PerfilTransaccional({ clienteId, onChange }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const canMonitor = MONITOR_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ monto_max_mensual_estimado: "", num_operaciones_estimadas: "", origen_recursos: "", fecha_perfil: "", notas: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pld/perfil/${clienteId}`);
      setData(data);
      const p = data.perfil || {};
      setForm({
        monto_max_mensual_estimado: p.monto_max_mensual_estimado ?? "",
        num_operaciones_estimadas: p.num_operaciones_estimadas ?? "",
        origen_recursos: p.origen_recursos ?? "",
        fecha_perfil: p.fecha_perfil ?? new Date().toISOString().slice(0, 10),
        notas: p.notas ?? "",
      });
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (!form.monto_max_mensual_estimado || Number(form.monto_max_mensual_estimado) <= 0) {
      toast.error("Captura el monto máximo mensual estimado"); return;
    }
    setBusy(true);
    try {
      await api.post("/pld/perfil", {
        cliente_id: clienteId,
        monto_max_mensual_estimado: Number(form.monto_max_mensual_estimado),
        num_operaciones_estimadas: Number(form.num_operaciones_estimadas || 0),
        origen_recursos: form.origen_recursos,
        fecha_perfil: form.fecha_perfil,
        notas: form.notas,
      });
      toast.success("Perfil transaccional estimado guardado (Disposición 21ª)");
      setEdit(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar el perfil"); }
    finally { setBusy(false); }
  };

  const recalcular = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pld/perfil/${clienteId}/recalcular`);
      toast.success(data.alerta_generada
        ? "Perfil recalculado. Se generó una alerta por desviación para el Oficial de Cumplimiento."
        : "Perfil real recalculado desde la operación.");
      await load();
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo recalcular"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando perfil transaccional...</div>;

  const perfil = data?.perfil;
  const calc = perfil?.perfil_calculado;
  const desv = perfil?.desviacion;
  const tieneEstimado = perfil && Number(perfil.monto_max_mensual_estimado) > 0;
  // oct-2026 — Origen declarado en el ALTA (select de cliente/prospecto SOFOM). Si además hay
  // Perfil transaccional de PLD capturado (`perfil.origen_recursos`), ese SIEMPRE prevalece
  // (comportamiento previo, sin cambio). Es solo RESPALDO de lectura: nunca se escribe aquí.
  const origenAlta = data?.cliente?.origen_recursos
    ? (String(data.cliente.origen_recursos).trim().toLowerCase() === "otro"
        && data.cliente.origen_recursos_detalle
        ? `Otro: ${data.cliente.origen_recursos_detalle}`
        : data.cliente.origen_recursos)
    : "";
  const origenMostrado = perfil?.origen_recursos || origenAlta;

  return (
    <div className="space-y-4" data-testid="perfil-transaccional">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <h4 className="font-heading text-sm font-semibold text-slate-700">Perfil transaccional (43ª-V / 21ª)</h4>
        </div>
        <div className="flex gap-2">
          {canWrite && !edit && (
            <Button data-testid="perfil-editar-btn" size="sm" variant="outline" onClick={() => setEdit(true)}>
              <Pencil className="mr-1.5 h-4 w-4" /> {tieneEstimado ? "Editar estimado" : "Capturar estimado"}
            </Button>
          )}
          {canMonitor && (
            <Button data-testid="perfil-recalcular-btn" size="sm" onClick={recalcular} disabled={busy} className="bg-accent hover:bg-forest-700">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Recalcular real
            </Button>
          )}
        </div>
      </div>

      {/* Banner de desviación (21ª) */}
      {desv?.hay_desviacion && (
        <div data-testid="perfil-desviacion-banner" className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Posible operación inusual por desviación de perfil ({desv.nivel})</p>
            {(desv.desviaciones || []).map((d, i) => <p key={i} className="text-amber-700">{d.detalle}</p>)}
            <p className="mt-1 text-xs text-amber-700">Se sugiere reevaluar el grado de riesgo (EBR). Alerta enviada al Oficial de Cumplimiento.</p>
          </div>
        </div>
      )}
      {data?.cliente?.perfil_revisar_riesgo && !desv?.hay_desviacion && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <ShieldAlert className="h-4 w-4" /> Este cliente fue marcado para reevaluar su grado de riesgo (EBR) por desviación de perfil.
        </div>
      )}

      {/* Edición del estimado */}
      {edit ? (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="perfil-form">
          <p className="text-xs text-slate-500">Perfil declarado por el cliente al inicio de la relación (al menos los primeros 6 meses, Disposición 21ª).</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="mb-1 block text-xs">Monto máximo mensual estimado (MXN)</Label>
              <Input data-testid="perfil-monto-input" type="number" value={form.monto_max_mensual_estimado} onChange={(e) => set("monto_max_mensual_estimado", e.target.value)} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Nº de operaciones esperadas / mes</Label>
              <Input data-testid="perfil-ops-input" type="number" value={form.num_operaciones_estimadas} onChange={(e) => set("num_operaciones_estimadas", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs">Origen de los recursos</Label>
              <Input data-testid="perfil-origen-input" value={form.origen_recursos} onChange={(e) => set("origen_recursos", e.target.value)} placeholder="Sueldo, actividad empresarial, honorarios..." />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Fecha del perfil</Label>
              <Input type="date" value={form.fecha_perfil} onChange={(e) => set("fecha_perfil", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs">Notas</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => set("notas", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEdit(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="perfil-guardar-btn" size="sm" onClick={guardar} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Estimado */}
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="perfil-estimado-card">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Estimado (declarado)</p>
            {origenMostrado && (
              <div className="mb-2 flex justify-between text-sm" data-testid="perfil-origen-recursos-row">
                <span className="text-slate-500">Origen de recursos</span>
                <span className="text-right">
                  {origenMostrado}
                  {!perfil?.origen_recursos && (
                    <span className="ml-1 text-[10px] text-slate-400">(del alta)</span>
                  )}
                </span>
              </div>
            )}
            {tieneEstimado ? (
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Monto máx. mensual</dt><dd className="font-mono font-semibold">{mxn(perfil.monto_max_mensual_estimado)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Operaciones/mes</dt><dd className="font-mono">{perfil.num_operaciones_estimadas || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Fecha del perfil</dt><dd>{fdate(perfil.fecha_perfil)}</dd></div>
              </dl>
            ) : (
              <p className="text-sm text-slate-400">Sin perfil estimado capturado. {canWrite ? "Captúralo con el botón de arriba." : ""}</p>
            )}
          </div>
          {/* Real calculado */}
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="perfil-real-card">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent">Real (calculado de la operación)</p>
            {calc ? (
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Monto mensual máx.</dt><dd className="font-mono font-semibold">{mxn(calc.monto_mensual_max)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Promedio mensual</dt><dd className="font-mono">{mxn(calc.monto_mensual_promedio)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Ops máx./mes</dt><dd className="font-mono">{calc.num_operaciones_max_mes}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Efectivo acumulado</dt><dd className="font-mono">{mxn(calc.total_efectivo)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Meses con actividad</dt><dd>{calc.meses_con_actividad}</dd></div>
              </dl>
            ) : (
              <p className="text-sm text-slate-400">Sin cálculo aún. {canMonitor ? "Usa “Recalcular real”." : ""}</p>
            )}
          </div>
        </div>
      )}

      {/* Alertas de desviación del cliente */}
      {data?.alertas?.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <p className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Alertas por desviación de perfil</p>
          <table className="w-full text-left text-sm">
            <tbody>
              {data.alertas.map((a) => (
                <tr key={a.id} className="border-b border-slate-50" data-testid={`perfil-alerta-${a.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{a.folio}</td>
                  <td className="px-3 py-2"><StatusBadge value={a.nivel_riesgo} /></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{a.descripcion}</td>
                  <td className="px-3 py-2"><StatusBadge value={a.estatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
